import GoogleMaps from '@allesauto/core/googleMaps';
import TextLink from '@allesauto/core/textLink';
import translate from '@allesauto/i18n';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import React, { FC, useEffect, useState } from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { GoogleMapsEmbeddingName } from '@/features/CCM/utils/const';
import { hasCCMEmbedding } from '@/features/CCM/utils/embeddings';

export interface LocationMapProps {
  lat?: number | string | null;
  lng?: number | string | null;
  zoom?: number;
  width: string;
  height: string;
  className?: string;
  isDarkBg?: boolean;
}

const LocationMap: FC<React.PropsWithChildren<LocationMapProps>> = ({
  lat,
  lng,
  zoom,
  height,
  width,
  className,
  isDarkBg,
}) => {
  const intl = useIntl();
  const [hasConsent, setHasConsent] = useState(
    process.env.NODE_ENV === 'development'
  );

  useEffect(() => {
    function updateConsent() {
      if (
        window.CCM?.acceptedEmbeddings &&
        Array.isArray(window.CCM.acceptedEmbeddings)
      ) {
        setHasConsent(hasCCMEmbedding(GoogleMapsEmbeddingName));
      }
    }

    updateConsent();

    window.addEventListener('ccm19WidgetLoaded', updateConsent);
    window.addEventListener('ccm19WidgetClosed', updateConsent);

    return () => {
      window.removeEventListener('ccm19WidgetLoaded', updateConsent);
      window.removeEventListener('ccm19WidgetClosed', updateConsent);
    };
  }, []);

  const openCookieManagerOverlay = useCallback(() => {
    if (!window.CCM) {
      return;
    }

    window.CCM.openControlPanel();
  }, []);

  const apiKey =
    process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
    process.env.GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return null;
  }

  const latitude = isNil(lat) ? 0 : typeof lat === 'string' ? Number(lat) : lat;
  const longitude = isNil(lng)
    ? 0
    : typeof lng === 'string'
    ? Number(lng)
    : lng;

  return (
    <div className={className} data-hide-for-print="">
      <GoogleMaps
        apiKey={apiKey}
        lat={latitude}
        lng={longitude}
        zoom={zoom}
        width={width}
        height={height}
        blockingOverlay={
          !hasConsent ? (
            <div className="flex flex-col items-center justify-center">
              <div className={clsx(isDarkBg ? 'text-white' : 'text-grey80')}>
                {translate(intl, 'global.COOKIES.GOOGLE_MAPS_COOKIES')}
              </div>
              <TextLink
                icon={null}
                isDarkBg={isDarkBg}
                text={translate(intl, 'global.COOKIES.ACCEPT_COOKIES_LINK')}
                onClick={openCookieManagerOverlay}
              />
            </div>
          ) : undefined
        }
      />
    </div>
  );
};

export default LocationMap;
